import { companies } from './index';

/**
 * The company profile, fetched at most once per short window.
 *
 * Both the sidebar and the layout need it on every page load — the sidebar for
 * the company logo and name, the layout to decide whether to show the
 * "finish setup" banner — so every navigation made the same request twice.
 * That is cheap on a fast link and not cheap at all on a few hundred
 * milliseconds of round-trip, which is what this app actually runs over.
 *
 * Two things are going on here:
 *
 *   - IN-FLIGHT DEDUPE. Callers that arrive while a request is open share it,
 *     rather than starting a second one. This is what removes the duplicate.
 *   - A SHORT TTL. Long enough to cover a page's mount storm, short enough that
 *     a profile edited in Company Settings shows up almost immediately.
 *
 * `refreshCompanyProfile()` drops the cache for the cases that must not wait —
 * finishing the setup wizard, saving company settings — and `resetCompanyProfile()`
 * is called on logout, because the next person to sign in is a different company.
 */
const TTL_MS = 30 * 1000;

let cached: { value: Promise<any>; expiresAt: number } | null = null;

export const getCompanyProfileShared = (): Promise<any> => {
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  const value = companies.getProfile().catch((err) => {
    // A failure must not be cached, or one blip blinds the sidebar and the
    // setup banner for the whole TTL.
    cached = null;
    throw err;
  });

  cached = { value, expiresAt: Date.now() + TTL_MS };
  return value;
};

/** Force the next read to hit the API. */
export const refreshCompanyProfile = (): Promise<any> => {
  cached = null;
  return getCompanyProfileShared();
};

/** Drop it entirely. Call on logout. */
export const resetCompanyProfile = (): void => {
  cached = null;
};
